import { getInitials } from "@frontend-shared/utils";
import { MoreVertical, Trash2 } from "lucide-react";
import { memo, useCallback } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import type { IShareAccessRowProps } from "./types";

export const ShareAccessRow = memo(
  ({
    entry,
    shouldShowMenu,
    isRemoving,
    onRemove,
    isSelectable,
    isSelected,
    isBulkPending,
    onSelectChange,
    shouldReserveCheckboxSpace,
    shouldReserveMenuSpace,
    labels,
  }: IShareAccessRowProps) => {
    const displayName = entry.fullName || entry.email || labels.unknownUser;
    const initials = getInitials(displayName);

    const handleSelectChange = useCallback(
      (checked: boolean | "indeterminate") => {
        onSelectChange(entry.id, checked === true);
      },
      [onSelectChange, entry.id],
    );

    return (
      <div
        className="flex items-center justify-between gap-3"
        data-testid={`share-row-${entry.id}`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {isSelectable ? (
            <Checkbox
              checked={isSelected}
              onCheckedChange={handleSelectChange}
              disabled={isBulkPending || isRemoving}
              aria-label={labels.formatSelectAria(displayName)}
              data-testid={`share-row-select-${entry.id}`}
            />
          ) : (
            shouldReserveCheckboxSpace && (
              <div className="h-4 w-4 shrink-0" aria-hidden="true" />
            )
          )}
          <Avatar className="h-9 w-9 shrink-0 border-0 bg-primary/10">
            <AvatarImage src="" />
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
              {entry.avatarIcon ?? initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p
              dir="auto"
              className="truncate text-sm font-medium rtl:text-right"
            >
              {displayName}
            </p>
            <p
              dir="auto"
              className="truncate text-xs text-muted-foreground rtl:text-right"
            >
              {entry.email || ""}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Badge className="bg-primary/10 hover:bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
            {entry.badgeLabel ?? labels.permissionBadge}
          </Badge>

          {shouldShowMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-9"
                  disabled={isRemoving || isBulkPending}
                  data-testid={`share-row-menu-${entry.id}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="cursor-pointer text-destructive focus:text-destructive"
                  onClick={() => onRemove(entry.id, entry.isCurrentUser)}
                  data-testid={`share-row-remove-${entry.id}`}
                >
                  <Trash2 className="me-2 h-4 w-4" />
                  {entry.isCurrentUser ? labels.leaveLabel : labels.removeLabel}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* NOTE: NSAI-6773 — a row without a menu still holds the menu's slot
              when another row shows one, so every badge (Host included) lines
              up in one column. */}
          {!shouldShowMenu && shouldReserveMenuSpace && (
            <div className="h-8 w-9 shrink-0" aria-hidden="true" />
          )}
        </div>
      </div>
    );
  },
);

ShareAccessRow.displayName = "ShareAccessRow";
